import React, { useEffect, useState, useCallback } from 'react';
import { Image as ImageIcon, Video, Music, Eye, Share2, Download, ThumbsUp, ThumbsDown, MessageCircle, Dices, BarChart2, Trash2, X, Loader2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useStorageUrl } from '../chatHome/MemberAvatar';
import MediaPlayerLightbox from '../chatHome/MediaPlayerLightbox';
import {
  listMyMediaAssets, getMediaAssetStats, renameMediaAsset, deleteMediaAsset, likeMediaShare, mediaShareUrl,
  type MediaAssetSummary, type MediaAssetStats,
} from '../../services/mediaShareService';
import { fetchAsBlob } from '../../services/storageService';
import { useConfirm } from '../../hooks/useConfirm';

type Kind = 'image' | 'video' | 'audio';

// Client-side "give it a name" generator for the dice — mirrors the memorable group-name style.
const ADJ = ['amber', 'azure', 'copper', 'dusky', 'golden', 'indigo', 'lunar', 'misty', 'neon', 'quiet', 'rustic', 'solar', 'teal', 'velvet', 'wild'];
const NOUN = ['arc', 'bloom', 'cascade', 'cinder', 'drift', 'ember', 'glow', 'harbor', 'lumen', 'muse', 'orbit', 'pulse', 'ripple', 'spark', 'tide'];
const randomName = () => `${ADJ[Math.floor(Math.random() * ADJ.length)]}-${NOUN[Math.floor(Math.random() * NOUN.length)]}`;

/**
 * One creation tile: preview on top, then an EDITABLE title (default = the short id) with a dice
 * for a random name, the group it came from as a linked caption, and an action row underneath —
 * view, share, download, like / not-for-me, and insights. No "likes" count (reactions aren't a
 * scoreboard here).
 */
const AssetTile: React.FC<{ a: MediaAssetSummary; onStats: () => void; onRenamed: (name: string | null) => void; onDeleted: () => void }> = ({ a, onStats, onRenamed, onDeleted }) => {
  const url = useStorageUrl(a.storage_path);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(a.name || '');
  const [view, setView] = useState(false);
  const [vote, setVote] = useState<null | boolean>(null);
  const [copied, setCopied] = useState(false);
  const [confirm, confirmDialog] = useConfirm();
  const title = a.name || a.mediaId.slice(0, 8);

  const save = async (next: string) => {
    const v = next.trim();
    setEditing(false);
    onRenamed(v || null); // optimistic
    try { await renameMediaAsset(a.mediaId, v); } catch { /* keep the optimistic value */ }
  };
  const dice = () => { const n = randomName(); setDraft(n); save(n); };
  const share = async () => {
    try { await navigator.clipboard.writeText(mediaShareUrl(a.mediaId)); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* clipboard blocked */ }
  };
  const [downloading, setDownloading] = useState(false);
  const download = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      // Fetch via our SAME-ORIGIN /m/{id}/raw stream — fetching the cross-origin firebasestorage URL
      // is CORS-blocked, which silently failed the download. /raw is served by our function.
      const blob = await fetchAsBlob(`${mediaShareUrl(a.mediaId)}/raw`);
      const u = URL.createObjectURL(blob);
      const link = document.createElement('a'); link.href = u; link.download = title; link.click();
      URL.revokeObjectURL(u);
    } catch { /* network/blocked — leave the tile as-is */ } finally { setDownloading(false); }
  };
  const react = async (up: boolean) => { setVote(up); try { await likeMediaShare(a.mediaId, up); } catch { /* ignore */ } };
  const [deleting, setDeleting] = useState(false);
  const remove = async () => {
    if (deleting) return;
    if (!(await confirm({
      title: 'Delete this creation?',
      description: 'It is removed from your Saved Creations and its share link stops working. The original in the chat is kept.',
      confirmText: 'Delete',
      variant: 'destructive',
    }))) return;
    setDeleting(true);
    onDeleted(); // optimistic remove
    try { await deleteMediaAsset(a.mediaId); } catch { /* the listener/reload restores it on failure */ } finally { setDeleting(false); }
  };

  const ICON = 'hover:text-secondary-900 dark:hover:text-white transition-colors';
  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-800/50">
      <button onClick={() => url && setView(true)} className="group relative aspect-square bg-secondary-100 dark:bg-secondary-700" title="View">
        {a.kind === 'image' && url && <img src={url} alt={title} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />}
        {a.kind === 'video' && url && <video src={url} muted playsInline preload="metadata" className="absolute inset-0 h-full w-full object-cover" />}
        {(a.kind === 'audio' || !url) && (
          <div className="absolute inset-0 flex items-center justify-center text-secondary-400">
            {a.kind === 'audio' ? <Music className="w-8 h-8" /> : a.kind === 'video' ? <Video className="w-8 h-8" /> : <ImageIcon className="w-8 h-8" />}
          </div>
        )}
      </button>

      <div className="space-y-1.5 p-2.5">
        {/* Editable title + dice */}
        <div className="flex items-center gap-1">
          {editing ? (
            <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={() => save(draft)}
              onKeyDown={(e) => { if (e.key === 'Enter') save(draft); if (e.key === 'Escape') setEditing(false); }} maxLength={80}
              className="min-w-0 flex-1 rounded border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-900 px-1.5 py-0.5 text-xs text-secondary-900 dark:text-white focus:outline-none focus:border-secondary-500" />
          ) : (
            <button onClick={() => { setDraft(a.name || ''); setEditing(true); }} title="Rename"
              className="min-w-0 flex-1 truncate text-left text-sm font-semibold text-secondary-900 dark:text-white hover:underline">{title}</button>
          )}
          <button onClick={dice} title="Give it a random name" className={`shrink-0 text-secondary-400 ${ICON}`}><Dices className="w-3.5 h-3.5" /></button>
        </div>

        {/* Group it came from — small linked caption */}
        {a.group_title && (
          <a href={`/group/${a.group_slug || a.conversation_id || ''}`}
            className="block truncate text-[11px] text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-200">
            from {a.group_title}
          </a>
        )}

        {/* Action row */}
        <div className="flex items-center gap-2.5 pt-0.5 text-secondary-400">
          <button onClick={() => url && setView(true)} title="View" className={ICON}><Eye className="w-4 h-4" /></button>
          <button onClick={share} title={copied ? 'Link copied' : 'Copy share link'} className={`${ICON} ${copied ? 'text-secondary-900 dark:text-white' : ''}`}><Share2 className="w-4 h-4" /></button>
          <button onClick={download} disabled={downloading} title="Download" className={ICON}>{downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}</button>
          <button onClick={() => react(true)} title="Like" className={`${ICON} ${vote === true ? 'text-secondary-900 dark:text-white' : ''}`}><ThumbsUp className="w-4 h-4" /></button>
          <button onClick={() => react(false)} title="Not for me" className={`${ICON} ${vote === false ? 'text-secondary-900 dark:text-white' : ''}`}><ThumbsDown className="w-4 h-4" /></button>
          <a href={mediaShareUrl(a.mediaId)} target="_blank" rel="noreferrer" title="Comments" aria-label="Comments" className={ICON}><MessageCircle className="w-4 h-4" /></a>
          <button onClick={remove} disabled={deleting} title="Delete creation" className={`ml-auto hover:text-error-600 transition-colors ${deleting ? 'opacity-50' : ''}`}>{deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}</button>
          <button onClick={onStats} title={`${a.unique_viewers} unique viewers`} className={`inline-flex items-center gap-0.5 ${ICON}`}><BarChart2 className="w-3.5 h-3.5" /><span className="text-[11px]">{a.unique_viewers}</span></button>
        </div>
      </div>

      {view && url && <MediaPlayerLightbox url={url} kind={a.kind} name={title} editablePath={a.kind === 'image' ? a.storage_path : null} onClose={() => setView(false)} />}
      {confirmDialog}
    </div>
  );
};

const TABS: { kind: Kind; label: string; icon: React.ReactNode }[] = [
  { kind: 'image', label: 'Images', icon: <ImageIcon className="w-4 h-4" /> },
  { kind: 'video', label: 'Movies', icon: <Video className="w-4 h-4" /> },
  { kind: 'audio', label: 'Audio', icon: <Music className="w-4 h-4" /> },
];

/** Per-asset insights modal — totals, a 7/30/90-day attention series, and top viewer countries. */
const AssetStatsModal: React.FC<{ mediaId: string; title: string; onClose: () => void }> = ({ mediaId, title, onClose }) => {
  const [days, setDays] = useState<7 | 30 | 90>(30);
  const [stats, setStats] = useState<MediaAssetStats | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true; setLoading(true);
    getMediaAssetStats(mediaId, days).then((s) => { if (alive) setStats(s); }).catch(() => undefined).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [mediaId, days]);
  const geo = stats ? Object.entries(stats.geo).sort((a, b) => b[1] - a[1]).slice(0, 6) : [];
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-700 shadow-xl max-h-[88dvh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-secondary-200 dark:border-secondary-800">
          <h2 className="text-sm font-semibold text-secondary-900 dark:text-white truncate">{title}</h2>
          <button onClick={onClose} aria-label="Close" className="text-secondary-400 hover:text-secondary-700 dark:hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-5">
          <div className="flex gap-6">
            <div><p className="text-2xl font-bold text-secondary-900 dark:text-white">{stats?.unique_viewers ?? 0}</p><p className="text-xs text-secondary-400">Unique viewers</p></div>
            <div><p className="text-2xl font-bold text-secondary-900 dark:text-white">{stats?.windowViews ?? 0}</p><p className="text-xs text-secondary-400">Views · {days}d</p></div>
          </div>
          <div className="flex gap-1">
            {([7, 30, 90] as const).map((d) => (
              <button key={d} onClick={() => setDays(d)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium ${days === d ? 'bg-secondary-900 text-white dark:bg-white dark:text-secondary-900' : 'text-secondary-500 hover:bg-secondary-100 dark:hover:bg-secondary-800'}`}>
                {d}d
              </button>
            ))}
          </div>
          <div className="h-44">
            {loading ? <div className="h-full flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-secondary-400" /></div>
              : (stats && stats.series.length) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={stats.series}>
                    <XAxis dataKey="day" tick={{ fontSize: 10 }} stroke="#9ca3af" tickFormatter={(d) => String(d).slice(5)} />
                    <YAxis tick={{ fontSize: 10 }} stroke="#9ca3af" allowDecimals={false} width={28} />
                    <Tooltip />
                    <Line type="monotone" dataKey="views" stroke="#111827" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <p className="text-xs text-secondary-400 text-center pt-8">No views yet in this window.</p>}
          </div>
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wide text-secondary-400 mb-2">Top viewer locations</p>
            {geo.length ? (
              <div className="space-y-1">
                {geo.map(([c, n]) => (
                  <div key={c} className="flex items-center justify-between text-sm text-secondary-700 dark:text-secondary-200">
                    <span>{c === 'XX' ? 'Unknown' : c}</span><span className="text-secondary-400">{n}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-secondary-400">No location data yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

const MediaAssetsSection: React.FC = () => {
  const [kind, setKind] = useState<Kind>('image');
  const [assets, setAssets] = useState<MediaAssetSummary[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [selected, setSelected] = useState<MediaAssetSummary | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    listMyMediaAssets(kind).then((r) => { setAssets(r.assets); setCursor(r.nextCursor); })
      .catch(() => { setAssets([]); setCursor(null); }).finally(() => setLoading(false));
  }, [kind]);
  useEffect(() => { load(); }, [load]);

  const loadMore = () => {
    if (!cursor || more) return;
    setMore(true);
    listMyMediaAssets(kind, cursor).then((r) => { setAssets((a) => [...a, ...r.assets]); setCursor(r.nextCursor); })
      .catch(() => undefined).finally(() => setMore(false));
  };

  const renamed = (mediaId: string, name: string | null) =>
    setAssets((prev) => prev.map((x) => (x.mediaId === mediaId ? { ...x, name } : x)));
  const removed = (mediaId: string) =>
    setAssets((prev) => prev.filter((x) => x.mediaId !== mediaId));

  return (
    <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-6 space-y-4">
      <h3 className="text-sm font-semibold text-secondary-900 dark:text-white uppercase tracking-wide">Saved Creations</h3>
      <div className="flex gap-1">
        {TABS.map((t) => (
          <button key={t.kind} onClick={() => setKind(t.kind)}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium ${kind === t.kind ? 'bg-secondary-900 text-white dark:bg-white dark:text-secondary-900' : 'text-secondary-500 hover:bg-secondary-100 dark:hover:bg-secondary-700'}`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-secondary-400" /></div>
      ) : assets.length === 0 ? (
        <p className="text-sm text-secondary-400 py-6 text-center">No saved {kind} yet. Share generated media from a chat to see it here.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {assets.map((a) => (
              <AssetTile key={a.mediaId} a={a} onStats={() => setSelected(a)} onRenamed={(name) => renamed(a.mediaId, name)} onDeleted={() => removed(a.mediaId)} />
            ))}
          </div>
          {cursor && (
            <div className="flex justify-center pt-1">
              <button onClick={loadMore} disabled={more}
                className="inline-flex items-center gap-2 rounded-lg border border-secondary-200 dark:border-secondary-700 px-4 py-2 text-sm font-medium text-secondary-700 dark:text-secondary-200 hover:bg-secondary-50 dark:hover:bg-secondary-700 disabled:opacity-50">
                {more && <Loader2 className="w-4 h-4 animate-spin" />} Load more
              </button>
            </div>
          )}
        </>
      )}

      {selected && <AssetStatsModal mediaId={selected.mediaId} title={selected.name || selected.mediaId.slice(0, 8)} onClose={() => setSelected(null)} />}
    </div>
  );
};

export default MediaAssetsSection;
